import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useOutletContext, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLang } from "@/lib/i18n";
import { BusinessAuthProvider } from "@/lib/businessAuth";
import { AIRPORTS, CANTONS, CITIES, S, p } from "@/lib/seoContent";
import ChauffeurHub from "@/pages/seo/ChauffeurHub";
import CityHub from "@/pages/seo/CityHub";
import CantonHub from "@/pages/seo/CantonHub";
import AirportHub from "@/pages/seo/AirportHub";
import AirportIndex from "@/pages/seo/AirportIndex";
import RoutesIndex from "@/pages/seo/RoutesIndex";
import RoutePage from "@/pages/seo/RoutePage";
import Home from "@/pages/Home";
import Booking from "@/pages/Booking";
import TrackEntry from "@/pages/TrackEntry";
import Tracking from "@/pages/Tracking";
import Fleet from "@/pages/Fleet";
import Business from "@/pages/Business";
import BusinessAuth from "@/pages/BusinessAuth";
import BusinessDashboard from "@/pages/BusinessDashboard";
import Chauffeurs from "@/pages/Chauffeurs";
import Services from "@/pages/Services";
import ServicePage from "@/pages/ServicePage";
import CityPage from "@/pages/CityPage";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import DriverLink from "@/pages/DriverLink";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import AdminBookingNew from "@/pages/admin/AdminBookingNew";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminDrivers from "@/pages/admin/AdminDrivers";
import AdminDriverDetail from "@/pages/admin/AdminDriverDetail";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminDispatch from "@/pages/admin/AdminDispatch";
import AdminQuotes from "@/pages/admin/AdminQuotes";
import AdminQuoteDetail from "@/pages/admin/AdminQuoteDetail";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminInvoiceDetail from "@/pages/admin/AdminInvoiceDetail";
import AdminBillingForm from "@/pages/admin/AdminBillingForm";
import QuotePublic from "@/pages/QuotePublic";
import InvoicePublic from "@/pages/InvoicePublic";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminFinance from "@/pages/admin/AdminFinance";
import { AdminBusinessList, AdminBusinessDetail } from "@/pages/admin/AdminBusiness";
import AdminCommunications from "@/pages/admin/AdminCommunications";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSeo from "@/pages/admin/AdminSeo";
import NotFound from "@/pages/NotFound";
import GuidesIndex from "@/pages/guides/GuidesIndex";
import GuideCluster from "@/pages/guides/GuideCluster";
import GuideArticle from "@/pages/guides/GuideArticle";
import GuidesAbout from "@/pages/guides/GuidesAbout";
import AdminGuides from "@/pages/admin/AdminGuides";
import AdminGuideEdit from "@/pages/admin/AdminGuideEdit";
import { ABOUT_SLUG, CLUSTERS, GUIDES_SLUG } from "@/lib/guides";

// ---------------------------------------------------------------- shell localisé (/fr /en /de)
function LocaleShell({ lang }) {
  const { setLang } = useLang();
  useEffect(() => { setLang(lang); }, [lang, setLang]);
  return <Outlet context={{ lang }} />;
}
const useSeoLang = () => useOutletContext().lang;

function SeoChauffeur() { return <ChauffeurHub lang={useSeoLang()} />; }
function SeoCity({ cityId }) { return <CityHub lang={useSeoLang()} cityId={cityId} />; }
function SeoCanton({ cantonId }) { return <CantonHub lang={useSeoLang()} cantonId={cantonId} />; }
function SeoAirport({ airportId }) { return <AirportHub lang={useSeoLang()} airportId={airportId} />; }
function SeoAirportIndex() { return <AirportIndex lang={useSeoLang()} />; }
function SeoRoutesIndex() { return <RoutesIndex lang={useSeoLang()} />; }
function SeoRoute() {
  const lang = useSeoLang();
  const { slug } = useParams();
  return <RoutePage lang={lang} slug={slug} />;
}

function SeoGuidesIndex() { return <GuidesIndex lang={useSeoLang()} />; }
function SeoGuidesAbout() { return <GuidesAbout lang={useSeoLang()} />; }
function SeoGuideCluster({ clusterId }) { return <GuideCluster lang={useSeoLang()} clusterId={clusterId} />; }
function SeoGuide() {
  const lang = useSeoLang();
  const { slug } = useParams();
  return <GuideArticle lang={lang} slug={slug} />;
}

// anciennes pages villes → nouveaux hubs (anti-cannibalisation)
const LEGACY_CITY_TO_HUB = { geneve: "geneve", lausanne: "lausanne", zurich: "zurich" };
function LegacyCity({ lang }) {
  const { slug } = useParams();
  const hub = LEGACY_CITY_TO_HUB[slug];
  if (hub) return <Navigate to={p(lang, S.chauffeur[lang], CITIES[hub].slug[lang])} replace />;
  return <CityPage />;
}
function LegacyCityRedirect() {
  const { slug } = useParams();
  const hub = LEGACY_CITY_TO_HUB[slug];
  if (hub) return <Navigate to={p("fr", S.chauffeur.fr, CITIES[hub].slug.fr)} replace />;
  return <Navigate to={`/fr/villes/${slug}`} replace />;
}
function LegacyService() {
  const { slug } = useParams();
  return <Navigate to={`/fr/services/${slug}`} replace />;
}

// un bloc de routes publiques par langue — jamais de mauvais croisement de slugs
const LocaleRoutes = (l) => (
  <Route key={l} path={`/${l}`} element={<LocaleShell lang={l} />}>
    <Route index element={<Home />} />
    <Route path={S.chauffeur[l]} element={<SeoChauffeur />} />
    {Object.keys(CITIES).map((cid) => (
      <Route key={cid} path={`${S.chauffeur[l]}/${CITIES[cid].slug[l]}`} element={<SeoCity cityId={cid} />} />
    ))}
    {Object.keys(CANTONS).map((cid) => (
      <Route key={cid} path={`${S.chauffeur[l]}/${CANTONS[cid].slug[l]}`} element={<SeoCanton cantonId={cid} />} />
    ))}
    <Route path={S.airport[l]} element={<SeoAirportIndex />} />
    {Object.keys(AIRPORTS).map((aid) => (
      <Route key={aid} path={`${S.airport[l]}/${AIRPORTS[aid].slug[l]}`} element={<SeoAirport airportId={aid} />} />
    ))}
    <Route path={S.routes[l]} element={<SeoRoutesIndex />} />
    <Route path={`${S.routes[l]}/:slug`} element={<SeoRoute />} />
    <Route path={GUIDES_SLUG[l]} element={<SeoGuidesIndex />} />
    <Route path={`${GUIDES_SLUG[l]}/${ABOUT_SLUG[l]}`} element={<SeoGuidesAbout />} />
    {Object.keys(CLUSTERS).map((cid) => (
      <Route key={`g-${cid}`} path={`${GUIDES_SLUG[l]}/${CLUSTERS[cid].slug[l]}`} element={<SeoGuideCluster clusterId={cid} />} />
    ))}
    <Route path={`${GUIDES_SLUG[l]}/:slug`} element={<SeoGuide />} />
    <Route path={S.fleet[l]} element={<Fleet />} />
    <Route path="services" element={<Services />} />
    <Route path="services/:slug" element={<ServicePage />} />
    <Route path="chauffeurs" element={<Chauffeurs />} />
    <Route path="faq" element={<Faq />} />
    <Route path={S.contact[l]} element={<Contact />} />
    <Route path="business" element={<Business />} />
    <Route path="villes" element={<Navigate to={p(l, S.chauffeur[l])} replace />} />
    <Route path="villes/:slug" element={<LegacyCity lang={l} />} />
    <Route path="*" element={<NotFound />} />
  </Route>
);

// À chaque changement de page, retour en haut (sauf navigation vers une ancre #section)
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <LanguageProvider>
      <BusinessAuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* redirections des anciennes URLs publiques (301 servis par serve.json en prod) */}
            <Route path="/" element={<Navigate to="/fr" replace />} />
            <Route path="/aeroports" element={<Navigate to="/fr/transfert-aeroport" replace />} />
            <Route path="/flotte" element={<Navigate to="/fr/flotte" replace />} />
            <Route path="/chauffeurs" element={<Navigate to="/fr/chauffeurs" replace />} />
            <Route path="/services" element={<Navigate to="/fr/services" replace />} />
            <Route path="/services/:slug" element={<LegacyService />} />
            <Route path="/villes" element={<Navigate to="/fr/chauffeur-prive" replace />} />
            <Route path="/villes/:slug" element={<LegacyCityRedirect />} />
            <Route path="/faq" element={<Navigate to="/fr/faq" replace />} />
            <Route path="/contact" element={<Navigate to="/fr/contact" replace />} />
            <Route path="/business" element={<Navigate to="/fr/business" replace />} />

            {/* pages localisées */}
            {["fr", "en", "de"].map(LocaleRoutes)}

            {/* flux fonctionnels — jamais préfixés (liens e-mails, tokens, apps) */}
            <Route path="/reservation" element={<Booking />} />
            <Route path="/suivi" element={<TrackEntry />} />
            <Route path="/suivi/:code" element={<Tracking />} />
            <Route path="/business/connexion" element={<BusinessAuth mode="login" />} />
            <Route path="/business/inscription" element={<BusinessAuth mode="signup" />} />
            <Route path="/business/app" element={<BusinessDashboard />} />
            <Route path="/chauffeur/:token" element={<DriverLink />} />
            <Route path="/devis/:token" element={<QuotePublic />} />
            <Route path="/facture/:token" element={<InvoicePublic />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/app" element={<AdminShell />}>
              <Route index element={<AdminOverview />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="bookings/new" element={<AdminBookingNew />} />
              <Route path="bookings/:id" element={<AdminBookingDetail />} />
              <Route path="dispatch" element={<AdminDispatch />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="customers/:id" element={<AdminCustomerDetail />} />
              <Route path="business" element={<AdminBusinessList />} />
              <Route path="business/:id" element={<AdminBusinessDetail />} />
              <Route path="drivers" element={<AdminDrivers />} />
              <Route path="drivers/:id" element={<AdminDriverDetail />} />
              <Route path="vehicles" element={<AdminVehicles />} />
              <Route path="quotes" element={<AdminQuotes />} />
              <Route path="quotes/new" element={<AdminBillingForm kind="quote" />} />
              <Route path="quotes/:id" element={<AdminQuoteDetail />} />
              <Route path="quotes/:id/edit" element={<AdminBillingForm kind="quote" />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="invoices/new" element={<AdminBillingForm kind="invoice" />} />
              <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
              <Route path="invoices/:id/edit" element={<AdminBillingForm kind="invoice" />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="communications" element={<AdminCommunications />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="guides" element={<AdminGuides />} />
              <Route path="guides/new" element={<AdminGuideEdit />} />
              <Route path="guides/:id" element={<AdminGuideEdit />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            {/* catch-all : vraie page 404 côté client (statut HTTP géré par 404.html au build) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
      </BusinessAuthProvider>
    </LanguageProvider>
  );
}

export default App;
